import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import FooterMain from "@shared/components/Atoms/Contents/FooterMain";
import MainNav from "@shared/components/Atoms/MainNav";
import { BLOG_LABEL } from "@shared/util/brand";
import "../Home/Home.css";
import "./BlogPost.css";

function formatDate(dateStr) {
  if (!dateStr) return "";
  try {
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return dateStr;
  }
}

function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios
      .get(`${import.meta.env.VITE_API_URL}/blog/${slug}`)
      .then((res) => setPost(res.data))
      .catch((err) => {
        if (err.response?.status === 404) {
          setError("Post not found.");
        } else {
          setError("Failed to load post.");
        }
      })
      .finally(() => setLoading(false));
  }, [slug]);

  return (
    <div className="home-page">
      <MainNav />
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-3xl mx-auto mt-[80px] mb-16">
          <Link
            to="/blog"
            className="text-gray-400 hover:text-gray-600 text-sm mb-8 inline-block transition-colors"
          >
            ← Back to {BLOG_LABEL}
          </Link>

          {loading && (
            <div className="flex justify-center py-16">
              <div className="w-8 h-8 border-4 border-gray-200 border-t-gray-600 rounded-full animate-spin" />
            </div>
          )}

          {error && (
            <div className="text-center py-16">
              <p className="text-red-500 mb-4">{error}</p>
              <Link to="/blog" className="text-blue-600 hover:underline">
                ← Back to {BLOG_LABEL}
              </Link>
            </div>
          )}

          {post && (
            <article>
              <header className="mb-8">
                <h1 className="text-4xl font-bold text-gray-900 mb-4 leading-tight">
                  {post.title}
                </h1>

                <div className="flex items-center gap-3 text-sm text-gray-400 mb-4">
                  {post.date && <span>{formatDate(post.date)}</span>}
                  {post.author && (
                    <>
                      <span>·</span>
                      <span>{post.author}</span>
                    </>
                  )}
                </div>

                {post.description && (
                  <p className="text-lg text-gray-500 border-l-4 border-gray-200 pl-4">
                    {post.description}
                  </p>
                )}

                {post.tags && post.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-4">
                    {post.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-2 py-1 bg-gray-100 text-gray-500 text-xs rounded-md"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </header>

              <div className="blog-post-content">
                <ReactMarkdown>{post.content}</ReactMarkdown>
              </div>

              <footer className="mt-12 pt-8 border-t border-gray-200">
                <Link
                  to="/blog"
                  className="text-blue-600 hover:underline text-sm font-medium"
                >
                  ← Back to {BLOG_LABEL}
                </Link>
              </footer>
            </article>
          )}
        </div>
      </div>
      <FooterMain />
    </div>
  );
}

export default BlogPost;
